import { Button } from "@egaranti/components";

import React, { useCallback, useMemo, useRef, useEffect, useState } from "react";
import { memo } from "react";
import { FixedSizeList as List } from "react-window";

import TableRow from "../components/TableRow";
import { COLUMN_WIDTHS } from "../constants";

import {
  AlertCircleIcon,
  CheckCircleIcon,
  LoaderIcon,
  PlusCircleIcon,
} from "lucide-react";

const DataValidationStep = ({
  tableRef,
  columnMapping,
  expectedColumns,
  filteredData,
  validationErrors,
  validationProgress = 0,
  firstErrorInfo = null,
  isProcessing = false,
  ROW_HEIGHT,
  LIST_HEIGHT,
  setProcessedData,
  onRevalidate,
  isDataDirty = false,
}) => {
  const listRef = useRef(null);
  const firstErrorRef = useRef(null);
  const headerRef = useRef(null);
  const [columnWidths, setColumnWidths] = useState({});

  const processedErrors = useMemo(() => {
    const errorsByRow = [];
    let firstError = null;

    validationErrors.forEach((error) => {
      const rowIndex = error.row - 2;
      if (rowIndex < 0) return; // Skip header row errors

      // Find or create row error entry
      let rowError = errorsByRow.find((err) => err.rowIndex === rowIndex);
      if (!rowError) {
        rowError = { rowIndex, errors: [] };
        errorsByRow.push(rowError);
      }

      // Add column error
      rowError.errors.push({
        key: error.column,
        message: error.message || "Geçersiz değer",
      });

      // Track first error for navigation
      if (!firstError) {
        firstError = {
          rowIndex,
          columnKey: error.column,
          message: error.message || "Geçersiz değer",
        };
        firstErrorRef.current = firstError;
      }
    });

    return { errors: errorsByRow, firstError };
  }, [validationErrors]);

  const handleCellChange = useCallback(
    (index, expectedKey, value) => {
      setProcessedData((prevData) => {
        if (!prevData || index < 0 || index >= prevData.length) {
          return prevData;
        }

        if (prevData[index]?.[expectedKey] === value) {
          return prevData;
        }

        return prevData.map((row, i) =>
          i === index ? { ...row, [expectedKey]: value } : row,
        );
      });
    },
    [setProcessedData],
  );

  const deleteRow = useCallback(
    (index) => {
      setProcessedData((prevData) => {
        if (!prevData || index < 0 || index >= prevData.length) {
          return prevData;
        }
        const newData = [...prevData];
        newData.splice(index, 1);
        return newData;
      });
    },
    [setProcessedData],
  );

  const addNewRow = useCallback(() => {
    const newRow = {};
    Object.keys(columnMapping).forEach((key) => {
      newRow[key] = "";
    });

    setProcessedData((prev) => [...prev, newRow]);

    setTimeout(() => {
      if (listRef.current) {
        try {
          listRef.current.scrollToItem(filteredData.length);
        } catch (err) {
          console.error("Scroll error:", err);
        }
      }
    }, 10);
  }, [columnMapping, filteredData.length, setProcessedData]);

  const navigateToFirstError = useCallback(() => {
    if (firstErrorInfo && listRef.current) {
      try {
        const rowIndex = Math.max(0, firstErrorInfo.row - 2);
        listRef.current.scrollToItem(rowIndex, "center");
      } catch (err) {
        console.error("Error navigating to first error:", err);
      }
    }
  }, [firstErrorInfo]);

  // Calculate optimal column widths based on content and headers
  const calculateColumnWidths = useCallback(() => {
    const widths = {};
    
    Object.entries(columnMapping).forEach(([key, column]) => {
      const headerText = expectedColumns.find((col) => col.key === key)?.label || key;
      
      // Calculate width based on header length and content
      let calculatedWidth = Math.max(
        headerText.length * 8 + 32, // Header text width + padding
        COLUMN_WIDTHS.MIN_WIDTH
      );
      
      // Check content width from first few rows for better width estimation
      if (filteredData && filteredData.length > 0) {
        const sampleRows = filteredData.slice(0, Math.min(10, filteredData.length));
        const maxContentLength = Math.max(
          ...sampleRows.map(row => {
            const value = row[key] || '';
            return String(value).length;
          }),
          0 // Fallback to 0 if no content
        );
        
        const contentWidth = Math.max(maxContentLength * 8 + 32, COLUMN_WIDTHS.MIN_WIDTH);
        calculatedWidth = Math.max(calculatedWidth, contentWidth);
      }
      
      // Apply max width limit
      widths[key] = Math.min(calculatedWidth, COLUMN_WIDTHS.MAX_WIDTH);
    });
    
    return widths;
  }, [columnMapping, expectedColumns, filteredData]);

  // Update column widths when data changes
  useEffect(() => {
    const newWidths = calculateColumnWidths();
    setColumnWidths(newWidths);
  }, [calculateColumnWidths]);

  const itemData = useMemo(() => {
    return {
      items: filteredData || [],
      columnMapping,
      columnWidths,
      handleCellChange,
      deleteRow,
      validation: {
        errors: processedErrors.errors,
        firstErrorRef,
      },
    };
  }, [
    filteredData,
    columnMapping,
    columnWidths,
    handleCellChange,
    deleteRow,
    processedErrors.errors,
  ]);

  return (
    <div className="flex flex-col">
      {isProcessing && (
        <div className="mb-4 flex items-center justify-center rounded-md bg-blue-50 p-4">
          <LoaderIcon className="mr-2 h-5 w-5 animate-spin text-blue-500" />
          <div>
            <p className="text-sm font-medium text-blue-700">
              Veriler doğrulanıyor... {validationProgress}%
            </p>
          </div>
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="mb-4 rounded-md bg-red-50 p-4">
          <div className="flex items-start">
            <AlertCircleIcon className="mr-2 mt-0.5 h-5 w-5 text-red-500" />
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-red-800">
                Toplamda {validationErrors.length} hata bulundu
              </p>
              <p className="mt-1 text-sm text-red-700">
                {firstErrorInfo ? (
                  <>
                    İlk hata: Satır {firstErrorInfo.row - 1}, "
                    {expectedColumns.find(
                      (col) => col.key === firstErrorInfo.column,
                    )?.label || firstErrorInfo.column}
                    " alanında: {firstErrorInfo.message}
                  </>
                ) : (
                  "Lütfen aşağıdaki hataları düzeltip tekrar deneyin."
                )}
              </p>
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={navigateToFirstError}
                  className="text-xs font-medium text-red-500 underline hover:font-semibold"
                >
                  İlk Hataya Git
                </button>
                <button
                  onClick={onRevalidate}
                  disabled={isProcessing}
                  className="text-xs font-medium text-red-500 underline hover:font-semibold"
                >
                  Tekrar Doğrula
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isDataDirty && (
        <div className="mb-4 rounded-md bg-yellow-50 p-4">
          <div className="flex items-start">
            <AlertCircleIcon className="mr-2 mt-0.5 h-5 w-5 text-yellow-500" />
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-yellow-800">
                Veri değişikliği yapıldı
              </p>
              <p className="mt-1 text-sm text-yellow-700">
                Veriler son doğrulama işleminden sonra değiştirildi. Yüklemek için tekrar doğrulama yapılması gerekiyor.
              </p>
              <div className="mt-2">
                <button
                  onClick={onRevalidate}
                  disabled={isProcessing}
                  className="text-xs font-medium text-yellow-600 underline hover:font-semibold"
                >
                  Tekrar Doğrula
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {validationProgress === 100 &&
        validationErrors.length === 0 &&
        filteredData.length > 0 &&
        !isDataDirty && (
          <div className="mb-4 rounded-md bg-green-50 p-4">
            <div className="flex items-start">
              <CheckCircleIcon className="mr-2 mt-0.5 h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm font-medium text-green-800">
                  Doğrulama başarılı
                </p>
                <p className="mt-1 text-sm text-green-700">
                  {filteredData.length} satır başarıyla doğrulandı.
                </p>
              </div>
            </div>
          </div>
        )}

      <div
        ref={tableRef}
        className="overflow-hidden rounded-lg border border-gray-200"
      >
        {/* Table Container with Horizontal Scroll */}
        <div className="overflow-x-auto overflow-y-hidden">

          {/* Table with Header and Body in same scroll container */}
          <div style={{ minWidth: 'max-content' }}>
            {/* Table Header */}
            <div 
              ref={headerRef}
              className="flex items-stretch border-b border-gray-200 bg-gray-50 sticky top-0 z-10"
            >
              <div
                className="flex items-center justify-center border-r border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 flex-shrink-0 bg-gray-50"
                style={{
                  width: `${COLUMN_WIDTHS.ROW_NUMBER_WIDTH}px`,
                  minWidth: `${COLUMN_WIDTHS.ROW_NUMBER_WIDTH}px`,
                }}
              >
                #
              </div>
              {Object.entries(columnMapping).map(([key, column]) => {
                const headerText = expectedColumns.find((col) => col.key === key)?.label || key;
                const width = columnWidths[key] || 150;
                
                return (
                  <div
                    key={`header-${key}`}
                    className="flex items-center border-r border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 last:border-r-0 flex-shrink-0 bg-gray-50"
                    style={{
                      width: `${width}px`,
                      minWidth: `${width}px`,
                    }}
                    title={headerText}
                  >
                    <span className="truncate">{headerText}</span>
                  </div>
                );
              })}
              <div
                className="flex flex-shrink-0 items-center justify-center bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700"
                style={{ 
                  width: `${COLUMN_WIDTHS.ACTION_WIDTH}px`, 
                  minWidth: `${COLUMN_WIDTHS.ACTION_WIDTH}px` 
                }}
              >
                İşlem
              </div>
            </div>
            
            {/* Table Body */}
            {filteredData && filteredData.length > 0 ? (
              <List
                ref={listRef}
                height={LIST_HEIGHT - 44} // Adjust for header height
                itemCount={filteredData.length}
                itemSize={ROW_HEIGHT}
                width="100%"
                itemData={itemData}
                overscanCount={5}
              >
                {({ index, style }) => (
                  <TableRow
                    index={index}
                    style={style}
                    item={itemData.items[index]}
                    columnMapping={itemData.columnMapping}
                    columnWidths={itemData.columnWidths}
                    handleCellChange={itemData.handleCellChange}
                    deleteRow={itemData.deleteRow}
                    validation={itemData.validation}
                  />
                )}
              </List>
            ) : (
              <div className="flex h-32 items-center justify-center text-gray-500">
                Veri bulunmamaktadır. Yeni satır ekleyebilirsiniz.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <Button
          onClick={addNewRow}
          variant="secondaryColor"
          className="flex items-center gap-1"
        >
          <PlusCircleIcon className="h-4 w-4" />
          Yeni Satır Ekle
        </Button>
      </div>
    </div>
  );
};

export default memo(DataValidationStep);
